import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type BestsellersProps = {
  language: Language;
};
const filters = {
  ua: ['Усі', 'Аерозолі', 'Грунти', 'Захисні покриття', 'Автокосметика'],
  en: ['All', 'Aerosols', 'Primers', 'Protective Coatings', 'Auto Cosmetics']
};
const products = {
  ua: [{
    id: 1,
    name: 'Акриловий грунт 2K',
    benefit: 'Швидке висихання за 15 хвилин',
    category: 'Грунти'
  }, {
    id: 2,
    name: 'Лак прозорий HS',
    benefit: 'Глянцевий блиск преміум класу',
    category: 'Захисні покриття'
  }, {
    id: 3,
    name: 'Аерозоль базовий',
    benefit: 'Ідеальне покриття з першого шару',
    category: 'Аерозолі'
  }],
  en: [{
    id: 1,
    name: '2K Acrylic Primer',
    benefit: 'Quick drying in 15 minutes',
    category: 'Primers'
  }, {
    id: 2,
    name: 'HS Clear Coat',
    benefit: 'Premium glossy shine',
    category: 'Protective Coatings'
  }, {
    id: 3,
    name: 'Base Aerosol',
    benefit: 'Perfect coverage from first coat',
    category: 'Aerosols'
  }]
};
const sectionText = {
  ua: {
    title: 'Бестселери',
    cta: 'Детальніше',
    viewAll: 'Переглянути всі продукти'
  },
  en: {
    title: 'Bestsellers',
    cta: 'Learn More',
    viewAll: 'View All Products'
  }
};
export function Bestsellers({
  language
}: BestsellersProps) {
  const [activeFilter, setActiveFilter] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const filterList = filters[language];
  const productList = products[language];
  return <section ref={ref} className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-1">
      {/* Subtle background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.05] pointer-events-none" style={{
      background: 'radial-gradient(circle, #FFB81C 0%, transparent 70%)'
    }} data-id="element-2" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-16" data-id="element-4">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4" data-id="element-5">
            {t.title}
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto" data-id="element-6" />
        </motion.div>

        {/* Filters - Desktop only */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="hidden lg:flex flex-wrap justify-center gap-3 mb-12" data-id="element-7">
          {filterList.map((filter, index) => <button key={filter} onClick={() => setActiveFilter(index)} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeFilter === index ? 'bg-chamaleon-yellow text-chamaleon-black' : 'bg-transparent border border-white/20 text-gray-400 hover:text-white hover:border-chamaleon-yellow'}`} data-id="element-8">
              {filter}
            </button>)}
        </motion.div>

        {/* Products Grid - 3 items */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-9">
          {productList.map((product, index) => <motion.div key={product.id} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + index * 0.1
        }} whileHover={{
          y: -8
        }} className="group" data-id="element-10">
              <div className="glass-card rounded-xl sm:rounded-2xl overflow-hidden h-full" data-id="element-11">
                {/* Product Image Area */}
                <div className="relative h-48 sm:h-56 bg-gradient-to-br from-chamaleon-charcoal to-chamaleon-black overflow-hidden" data-id="element-12">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-13">
                    <motion.div whileHover={{
                  scale: 1.05
                }} transition={{
                  duration: 0.3
                }} className="relative w-28 h-36 sm:w-32 sm:h-40 rounded-xl bg-[#111111] border border-white/20 overflow-hidden" data-id="element-14">
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-chamaleon-yellow" data-id="element-15" />

                      <div className="absolute inset-0 flex items-center justify-center" data-id="element-16">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-chamaleon-yellow/20 flex items-center justify-center" data-id="element-17">
                          <span className="text-chamaleon-yellow font-bold text-xs sm:text-sm" data-id="element-18">
                            C
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-chamaleon-yellow/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-19" />
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6" data-id="element-20">
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 transition-colors" data-id="element-21">
                    {product.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 sm:mb-6 leading-relaxed" data-id="element-22">
                    {product.benefit}
                  </p>
                  <motion.a href="#" className="inline-flex items-center gap-2 text-chamaleon-yellow font-medium text-sm group/link" whileHover={{
                x: 5
              }} data-id="element-23">
                    {t.cta}
                    <ArrowRightIcon className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" data-id="element-24" />
                  </motion.a>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* View All Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="text-center" data-id="element-25">
          <motion.a href="#products" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-transparent border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 hover:border-chamaleon-yellow transition-all duration-300 text-sm sm:text-base" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-26">
            {t.viewAll}
            <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-27" />
          </motion.a>
        </motion.div>
      </div>
    </section>;
}